import { Lead } from '../types';
import { AppNotification } from '../components/NotificationBell';

/**
 * Resolves all leads referenced by a notification.
 * Works seamlessly for:
 * 1. Notifications with explicit `leadId` or `leadIds`
 * 2. Legacy / existing notifications without `leadId` by performing intelligent
 *    heuristic matching against company names, contact persons, and parenthetical names
 *    found in the notification's title and body.
 */
export function resolveLeadsForNotification(
  notification: AppNotification,
  leads: Lead[]
): Lead[] {
  if (!leads || leads.length === 0 || !notification) return [];

  const matchedLeads: Lead[] = [];
  const matchedIds = new Set<string>();

  // 1. Explicit leadId
  if (notification.leadId) {
    const directLead = leads.find((l) => l.id === notification.leadId);
    if (directLead) {
      matchedIds.add(directLead.id);
      matchedLeads.push(directLead);
    }
  }

  // 2. Explicit leadIds array
  if (notification.leadIds && Array.isArray(notification.leadIds)) {
    for (const lid of notification.leadIds) {
      if (!matchedIds.has(lid)) {
        const lead = leads.find((l) => l.id === lid);
        if (lead) {
          matchedIds.add(lead.id);
          matchedLeads.push(lead);
        }
      }
    }
  }

  // If explicit matches were found and not empty, return them
  if (matchedLeads.length > 0) {
    return matchedLeads;
  }

  // 3. Heuristic matching against title and body for legacy/unlinked notifications
  const title = (notification.title || '').trim();
  const body = (notification.body || '').trim();
  const fullText = `${title} ${body}`.toLowerCase();

  // 3a. Extract parenthetical strings: e.g. "(CALIFORNIA DAIRIES)", "(FRONTIER PRODUCE)"
  const parentheticalTerms: string[] = [];
  const parenRegex = /\(([^)]+)\)/g;
  let match: RegExpExecArray | null;
  while ((match = parenRegex.exec(body)) !== null) {
    const term = match[1].trim().toLowerCase();
    if (term.length >= 3) {
      parentheticalTerms.push(term);
    }
  }

  // Check parenthetical terms first (they are usually company names in follow-up alerts)
  for (const term of parentheticalTerms) {
    for (const lead of leads) {
      if (matchedIds.has(lead.id)) continue;
      const comp = (lead.company || '').toLowerCase().trim();
      const cons = (lead.consigneeName || '').toLowerCase().trim();
      if (
        (comp.length >= 3 && (comp.includes(term) || term.includes(comp))) ||
        (cons.length >= 3 && (cons.includes(term) || term.includes(cons)))
      ) {
        matchedIds.add(lead.id);
        matchedLeads.push(lead);
      }
    }
  }

  // 3b. Direct company name & consignee matching
  for (const lead of leads) {
    if (matchedIds.has(lead.id)) continue;
    const comp = (lead.company || '').trim().toLowerCase();
    const cons = (lead.consigneeName || '').trim().toLowerCase();

    if (comp.length >= 4 && fullText.includes(comp)) {
      matchedIds.add(lead.id);
      matchedLeads.push(lead);
      continue;
    }

    if (cons.length >= 4 && fullText.includes(cons)) {
      matchedIds.add(lead.id);
      matchedLeads.push(lead);
      continue;
    }
  }

  // 3c. Direct contact name matching (require first + last name or at least 5 chars)
  for (const lead of leads) {
    if (matchedIds.has(lead.id)) continue;
    const name = (lead.name || lead.contactPerson || '').trim().toLowerCase();
    if (name.length >= 5 && fullText.includes(name)) {
      matchedIds.add(lead.id);
      matchedLeads.push(lead);
    }
  }

  return matchedLeads;
}

/**
 * Convenience helper returning the primary Lead ID for a notification, or undefined.
 */
export function resolvePrimaryLeadId(
  notification: AppNotification,
  leads: Lead[]
): string | undefined {
  if (notification.leadId) return notification.leadId;
  const resolved = resolveLeadsForNotification(notification, leads);
  return resolved[0]?.id;
}
